import { useTranslation } from 'react-i18next';
import { Link, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Container, type ContainerProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { SwitchLanguage } from '@/components/biz/switch-lang';
import { useCustom } from '@/hooks/use-custom';

const FluidContainer = styled(Container)<ContainerProps>(({ theme }) => ({
  maxWidth: '85% !important',
  width: '95%',
  padding: `0 ${theme.spacing(2)} !important`,
  [theme.breakpoints.up('lg')]: {
    maxWidth: `${theme.breakpoints.values.xl * 1.25}px !important`,
  },
  [theme.breakpoints.down('lg')]: {
    maxWidth: `${theme.breakpoints.values.md * 0.9}px !important`,
  },
  [theme.breakpoints.down('md')]: {
    maxWidth: `${theme.breakpoints.values.md * 1}px !important`,
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: theme.breakpoints.values.sm,
    padding: `0 !important`,
    width: '100%',
  },
}));

const CenterContainer = styled(Container)<ContainerProps>(({ theme }) => ({
  display: 'flex',
  maxWidth: '90%',
  width: '90%',
  padding: 0,
  [theme.breakpoints.up('lg')]: {
    maxWidth: theme.breakpoints.values.xl * 0.85,
  },
  [theme.breakpoints.down('lg')]: {
    maxWidth: theme.breakpoints.values.md * 0.75,
  },
  [theme.breakpoints.down('md')]: {
    maxWidth: theme.breakpoints.values.md * 0.75,
  },
  [theme.breakpoints.down('sm')]: {
    padding: `0 !important`,
    maxWidth: theme.breakpoints.values.sm,
    width: '100%',
  },
}));

const FooterContainer: React.FC<ContainerProps> = (props) => {
  const { layoutConfig } = useCustom();

  return layoutConfig?.layout !== 'space-between' ? (
    <CenterContainer component="footer" {...props} />
  ) : (
    <FluidContainer component="footer" {...props} />
  );
};

export const Footer: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isUpMd = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <FooterContainer>
      <Stack
        direction={'row'}
        justifyContent="space-between"
        className="w-full"
        spacing={2}
        alignItems="center"
        px={1}
        py={{
          sm: 1,
          xs: 2,
        }}
      >
        {isUpMd && <SwitchLanguage />}

        <Stack
          spacing={0}
          direction="row"
          width={isUpMd ? 'auto' : '100%'}
          justifyContent={isUpMd ? 'flex-start' : 'space-between'}
          alignItems="center"
        >
          <Stack spacing={3} direction="row" justifyContent="flex-start" alignItems="center" px={2} sx={{ flex: 1 }}>
            <Link
              href={t('privacy_url')}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              color="inherit"
              variant="body2"
            >
              {t('privacy_statement')}
            </Link>
            <Link
              href={t('legal_url')}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              color="inherit"
              variant="body2"
            >
              {t('terms_of_service_link')}
            </Link>
          </Stack>
          <Typography px={2} variant="body2">
            ©Lenovo {new Date().getFullYear()}
          </Typography>
        </Stack>
      </Stack>
    </FooterContainer>
  );
};
